import ApiClient from "./ApiClient";
class ChatHistoryService {
    constructor() {
        this.baseUrl = '/api/chat-history';
    }

    /**
     * Create a new chat conversation
     * @param {number} sparkId - Spark ID
     * @param {string} sparkName - Spark name
     * @param {string} title - Chat title (first user message)
     * @param {object} metadata - Additional metadata
     */
    async createChat(sparkId, sparkName, title, integrations = [], agents = [], metadata = {}) {
        return await ApiClient.post(this.baseUrl, {
            sparkId,
            sparkName,
            title,
            integrations,
            agents,
            metadata
        });
    }

    /**
     * Get paginated chat history
     * @param {number} pageSize - Number of chats to fetch (default 30)
     * @param {string} continuationToken - Pagination token
     * @param {boolean} includeDeleted - Include Deleted Chats
     * @param {number} sparkId - Spark Id
     */
    async getChatHistory(pageSize = 30, continuationToken = null, includeDeleted = false, sparkId = null) {
        const params = new URLSearchParams({ pageSize: pageSize.toString() });
        if (continuationToken) {
            params.append('continuationToken', continuationToken);
        }
        params.append('includeDeleted', includeDeleted.toString());
        if (sparkId !== null) {
            params.append('sparkId', sparkId.toString());
        }

        const data = await ApiClient.get(`${this.baseUrl}?${params}`);
        data.chats = (data.chats || []).map(c => ({
            ...c,
            integrations: c.integrations || [],
            agents: c.agents || [],
            metadata: c.metadata || {}
        }));

        return data;
    }

    /**
     * Clear all chats for current user
     * @param {boolean} hardDelete - Permanently delete (default: false)
     */
    async clearAllChats(hardDelete = false) {
        const params = new URLSearchParams({ hardDelete: hardDelete.toString() });
        return await ApiClient.delete(`${this.baseUrl}?${params}`);
    }

    /**
     * Get specific chat by ID
     * @param {string} chatId - Chat ID
     */
    async getChatById(chatId) {
        return await ApiClient.get(`${this.baseUrl}/${chatId}`);
    }

    /**
     * Update chat metadata/context.
     * Supported fields: title, sparkId, sparkName, integrations, agents, metadata
     */
    async updateChat(chatId, updates) {
        return await ApiClient.patch(`${this.baseUrl}/${chatId}`, updates);
    }

    /**
     * Delete a specific chat
     * @param {string} chatId - Chat ID
     * @param {boolean} hardDelete - Permanently delete (default: false)
     */
    async deleteChat(chatId, hardDelete = false) {
        const params = new URLSearchParams({ hardDelete: hardDelete.toString() });
        return await ApiClient.delete(`${this.baseUrl}/${chatId}?${params}`);
    }

    /**
     * Get messages for a specific chat
     * @param {string} chatId - Chat ID
     * @param {number} pageSize - Number of messages to fetch
     * @param {string} continuationToken - Pagination token
     * @param {boolean} includeDeleted - Include Deleted Chats
     */
    async getChatMessages(chatId, pageSize = 100, continuationToken = null, includeDeleted = false) {
        const params = new URLSearchParams({ pageSize: pageSize.toString() });
        if (continuationToken) {
            params.append('continuationToken', continuationToken);
        }
        params.append('includeDeleted', includeDeleted.toString());

        return await ApiClient.get(`${this.baseUrl}/${chatId}/messages?${params}`);
    }

    /**
     * Add a message to a chat
     * @param {string} chatId - Chat ID
     * @param {string} role - Message role (user/assistant)
     * @param {string} content - Message content
     * @param {object} metadata - Additional metadata
     */
    async addMessage(chatId, role, content, model = null, metadata = {}) {
        const payload = { chatId, role, content, metadata };
        if (model) payload.model = model;

        return await ApiClient.post(`${this.baseUrl}/${chatId}/messages`, payload);
    }

    /**
     * Update message feedback (thumbs up/down)
     * @param {string} chatId - Chat ID
     * @param {string} messageId - Message ID
     * @param {string|null} feedback - Feedback value ('up', 'down', or null to clear)
     */
    async updateMessageFeedback(chatId, messageId, feedback) {
        const params = new URLSearchParams();
        if (feedback) {
            params.append('feedback', feedback);
        }

        return await ApiClient.patch(`${this.baseUrl}/${chatId}/messages/${messageId}/feedback?${params}`, {});
    }
}

const chatHistoryService = new ChatHistoryService();
export default chatHistoryService;